import { PageItemSkeleton } from '@/features/earn/components/PageItemSkeleton';
import { keyGenerator } from '@/utils/keyGenerator';
import { ActivityIndicator, SxProp, View } from 'dripsy';
import React from 'react';

import { TextBody } from '../typography';
import { TableViewCell } from './TableViewCell';
import { TableViewRow } from './TableViewRow';

const BORDER_RADIUS = 5;
const COLUMN_GAP = 40;
const DEFAULT_MIN_WIDTH = 150;
export interface TableViewColumn<T>
  extends Array<{
    index: keyof T;
    title: string;
    flex?: number;
    minWidth?: number;
    width?: number;
    align?: 'left' | 'right' | 'center';
    sx?: SxProp;
    render?: (item: T) => React.ReactNode;
    renderTitle?: (item: T) => React.ReactNode;
    formatValue?: (item: T) => string;
  }> {}

interface TableViewProps<T> {
  data: Array<T>;
  columns: TableViewColumn<T>;
  titleSx?: SxProp;
  rowVerticalAlign?: 'flex-start' | 'flex-end' | 'center';
  loading?: boolean;
  gap?: number;
  onRowPressed?: (item: T) => void;
  onRowUpdated?: (item: T) => SxProp;
  emptyComponent?: React.ReactElement;
  sx?: SxProp;
}

export const TableView = <T extends unknown>({
  columns,
  data,
  titleSx,
  rowVerticalAlign,
  loading,
  gap,
  onRowPressed,
  onRowUpdated,
  emptyComponent,
  sx
}: TableViewProps<T>) => {
  const DEFAULT_TITLE_BACKGROUND_COLOR = '#AFAFAF';
  const DEFAULT_COLUMN_GAP = gap ? gap : COLUMN_GAP;
  const TOTAL_GAP_WIDTH = columns.length * COLUMN_GAP + 4;
  const ROW_MIN_WIDTH = columns.reduce((acc, column) => acc + (column.width || column.minWidth || DEFAULT_MIN_WIDTH), TOTAL_GAP_WIDTH);

  return (
    <View
      sx={{
        overflowX: 'auto',
        borderRadius: 5,
        borderWidth: 1,
        borderColor: data.length > 0 ? '$borderButton' : 'transparent',
        ...sx
      }}>
      {loading && data.length > 0 && (
        <View
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'white',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: 0.5,
            zIndex: 1
          }}>
          {/* Activity Indicator */}
          <ActivityIndicator size="large" color="#0000ff" />
        </View>
      )}
      <View sx={{}}>
        {loading && data.length === 0 && <PageItemSkeleton />}
        {/* Title */}
        {data.length > 0 && (
          <View
            sx={{
              ...styles.tableRow,
              minWidth: ROW_MIN_WIDTH,
              paddingY: 0,
              backgroundColor: DEFAULT_TITLE_BACKGROUND_COLOR,
              borderTopRightRadius: BORDER_RADIUS,
              borderTopLeftRadius: BORDER_RADIUS,
              gap: DEFAULT_COLUMN_GAP,
              ...titleSx
            }}>
            {columns.map((column, i) => (
              <TableViewCell
                key={keyGenerator('')}
                align={column.align}
                flex={column.flex}
                width={column.width}
                sx={{
                  width: column.width,
                  minWidth: column.width || column.minWidth || DEFAULT_MIN_WIDTH,
                  paddingY: '$3',
                  borderTopLeftRadius: i === 0 ? BORDER_RADIUS : 0,
                  borderTopRightRadius: i === columns.length - 1 ? BORDER_RADIUS : 0,
                  ...column.sx
                }}>
                {column.renderTitle ? column.renderTitle : <TextBody sx={{ color: '$textDarkBackground' }}>{column.title}</TextBody>}
              </TableViewCell>
            ))}
          </View>
        )}
        {/* Table */}
        {data.map((item, i) => (
          <TableViewRow
            key={keyGenerator('')}
            item={item}
            index={i}
            columns={columns}
            rowVerticalAlign={rowVerticalAlign}
            rowMinWidth={ROW_MIN_WIDTH}
            onRowPressed={onRowPressed}
            onRowUpdated={onRowUpdated}
            gap={DEFAULT_COLUMN_GAP}
          />
        ))}
        {/* Empty */}
        {!loading && data.length === 0 && emptyComponent}
      </View>
    </View>
  );
};

enum StyleName {
  container = 'container',
  tableRow = 'tableRow',
  divider = 'divider'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flex: 1
  },
  divider: {
    height: 1,
    backgroundColor: '$borderButton'
  },
  tableRow: {
    padding: '$4',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '$5'
  }
};
